import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2, Zap } from 'lucide-react';

export const AiAdsService = () => {
  const whatWeOffer = [
    {
      title: 'Strategy',
      items: [
        'Define which AI ad platforms and placements make sense for your brand',
        'Align messaging with how people interact with AI tools',
        'Integration with your overall GEO/AIO efforts'
      ]
    },
    {
      title: 'Creative & Format',
      items: [
        'Design ads that feel native and helpful inside AI interfaces',
        'Write prompts, responses, and CTAs that perform in conversational formats',
        'A/B testing of different creative approaches'
      ]
    },
    {
      title: 'Setup & Management',
      items: [
        'Set up tracking, audiences, and experiments',
        'Manage bids, budgets, and creatives over time',
        'Continuous optimization based on performance data'
      ]
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-accent/10 text-accent border-accent/20">
                <Zap className="w-3 h-3 mr-1" />
                Emerging Channel
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
                AI Ads: Reach People{' '}
                <span className="text-gradient">Inside ChatGPT</span> and Other AI Experiences
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8">
                Prepare for the new wave of AI-native ad inventory—from ChatGPT and beyond—with campaigns built for trust, relevance, and performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-audit">
                  <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline">
                    Discuss Strategy
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1745674684468-b9fc392fda3f?w=800&h=600&fit=crop"
                alt="AI Ads Service"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Shift */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6 text-center">
            The AI Advertising Shift
          </h2>
          <p className="text-lg text-foreground leading-relaxed mb-6">
            OpenAI has confirmed that ads are coming to ChatGPT, and more AI platforms will follow. This opens a completely new environment to reach people when they're actively asking questions, researching solutions, and making decisions—inside the tools they use every day.
          </p>
          <p className="text-lg text-foreground leading-relaxed">
            Unlike traditional display ads, AI-native ads are conversational, contextual, and integrated into the AI's responses. This requires a completely different approach to messaging, targeting, and creative.
          </p>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            Our AI Ads Services
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {whatWeOffer.map((section, index) => (
              <Card key={index} className="border-border">
                <CardContent className="pt-6">
                  <h3 className="font-display font-semibold text-xl mb-4">{section.title}</h3>
                  <ul className="space-y-3">
                    {section.items.map((item, iIndex) => (
                      <li key={iIndex} className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration with GEO */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6 text-center">
            How AI Ads Connect with GEO
          </h2>
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="pt-6">
              <p className="text-foreground leading-relaxed">
                AI ads work best when your brand is also organically referenced by AI engines. Our GEO/AIO and AI ads services are designed to reinforce each other: organic recommendations make your ads more credible; well-targeted ads increase your brand's familiarity and prompt more AI citations.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            Why AI-Native Advertising?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">High Intent Audience</h3>
                <p className="text-muted-foreground">
                  Reach people at the exact moment they're asking for solutions, not passively scrolling.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">Contextual Relevance</h3>
                <p className="text-muted-foreground">
                  Ads are served based on the specific question or task, ensuring high relevance.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">Trust Building</h3>
                <p className="text-muted-foreground">
                  Being present in AI tools users trust daily enhances your brand credibility.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">Early Mover Advantage</h3>
                <p className="text-muted-foreground">
                  Get in before the space becomes crowded and CPCs rise significantly.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-8 text-center">
            AI Ads FAQs
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                Where can AI ads run today?
              </h3>
              <p className="text-muted-foreground">
                The landscape is evolving quickly. We focus on emerging AI-native inventory (such as ChatGPT and other assistants) plus AI-powered placements inside existing ad platforms. Our team keeps a live map of what is available and what&apos;s coming next.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How do you measure performance?
              </h3>
              <p className="text-muted-foreground">
                We track standard campaign metrics—impressions, clicks, and conversions—as well as assisted impact on AI citations and branded search. The goal is not just cheap clicks, but stronger presence when people ask AI tools for recommendations.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                What budgets do you work with?
              </h3>
              <p className="text-muted-foreground">
                Most clients start testing AI ads with a focused pilot budget, then scale as we validate performance. We&apos;ll help you design a test that&apos;s meaningful without committing your entire media spend on day one.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                Do AI ads replace our existing paid media programs?
              </h3>
              <p className="text-muted-foreground">
                No. We treat AI ads as a new, complementary channel. We&apos;ll help you carve out a smart test budget and make sure what we learn can inform (and be informed by) your existing paid search, social, and display efforts.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                What kind of creative works best in AI environments?
              </h3>
              <p className="text-muted-foreground">
                The best-performing creative feels like a helpful answer, not a banner. We focus on clear value props, proof, and next steps written in a conversational tone that fits the AI experience instead of interrupting it.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How do you keep us ahead as new AI ad formats launch?
              </h3>
              <p className="text-muted-foreground">
                We continuously track new AI inventory and run small experiments as formats emerge. You&apos;ll get regular recommendations on where to test next, so you&apos;re early in the most promising placements instead of chasing crowded channels later.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Prepare Your Brand for AI Ads
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Start with a free GEO/AIO audit and we&apos;ll include recommendations for AI ads strategy.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get Free Audit + AI Ads Strategy <span className="font-semibold">(Valued at $2,000+)</span>
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
